import { supabase } from './supabaseClient';

export interface SignUpInput {
  email: string;
  password: string;
  username: string;
  first_name?: string;
  last_name?: string;
  street_address?: string;
  city?: string;
  region?: string;
  postal_code?: string;
  country_code?: string;
}

function authCallbackUrl(query: Record<string, string> = {}): string {
  const appBase = new URL(import.meta.env.BASE_URL || '/', window.location.origin);
  const callback = new URL('auth/callback', appBase);
  for (const [k, v] of Object.entries(query)) {
    callback.searchParams.set(k, v);
  }
  return callback.toString();
}

export const authService = {
  async signUp({
    email, password, username,
    first_name, last_name, street_address, city, region, postal_code, country_code,
  }: SignUpInput) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: authCallbackUrl(),
        data: {
          username,
          first_name: first_name ?? null,
          last_name: last_name ?? null,
          street_address: street_address ?? null,
          city: city ?? null,
          region: region ?? null,
          postal_code: postal_code ?? null,
          country_code: country_code ?? null,
        },
      },
    });
    if (error) throw error;

    // After signup, update the profiles row with personal fields via RPC/direct update.
    // The profiles row is created by the Supabase trigger on auth.users insert;
    // we patch the extra fields immediately after signup.
    if (data.user) {
      const patch: Record<string, unknown> = { preferred_game_slug: 'pokemon' };
      if (first_name)     patch.first_name = first_name;
      if (last_name)      patch.last_name = last_name;
      if (street_address) patch.street_address = street_address;
      if (city)           patch.city = city;
      if (region)         patch.region = region;
      if (postal_code)    patch.postal_code = postal_code;
      if (country_code)   patch.country_code = country_code;
      // Swallow errors: profile may not exist yet if email confirmation is required.
      await supabase.from('profiles').update(patch).eq('id', data.user.id);
    }

    return data;
  },

  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  },

  async signInWithGoogle() {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: authCallbackUrl(),
        queryParams: { access_type: 'offline', prompt: 'consent' },
      },
    });
    if (error) throw error;
    return data;
  },

  async signInWithFacebook() {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'facebook',
      options: {
        redirectTo: authCallbackUrl(),
        scopes: 'email,public_profile',
      },
    });
    if (error) throw error;
    return data;
  },

  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  async resetPassword(email: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: authCallbackUrl({ type: 'recovery' }),
    });
    if (error) throw error;
  },

  async getSession() {
    const { data } = await supabase.auth.getSession();
    return data.session;
  },

  async getUser() {
    const { data } = await supabase.auth.getUser();
    return data.user;
  },

  onAuthChange(cb: (event: string, session: unknown) => void) {
    return supabase.auth.onAuthStateChange(cb);
  },
};
